import { meResponseSchema } from '@koderek/shared';
import { Alert, Anchor, Button, Loader, Stack, Text } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError, api } from '../api/client';
import { AuthCard } from '../features/auth/AuthCard';
import { ResendActivation } from '../features/auth/ResendActivation';
import { errorMessage, safeReturnTo, useSetMe } from '../features/auth/session';
import { t } from '../texts/pl';

/** US-202: link z maila aktywuje konto i loguje (AC-1). */
export function ActivatePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const setMe = useSetMe();
  const navigate = useNavigate();
  const started = useRef(false);
  const activate = useMutation({
    mutationFn: (value: string) => api('POST', '/auth/activate', { body: { token: value }, schema: meResponseSchema }),
    onSuccess: (me) => {
      setMe(me);
      // US-902 AC-2: powrót do agendy lub zajęć, z których zaczęła się rejestracja.
      const back = params.get('powrot');
      void navigate(back ? safeReturnTo(back) : '/konto', { replace: true });
    },
  });

  useEffect(() => {
    // Jedno wywołanie także w StrictMode (token jest jednorazowy).
    if (token && !started.current) {
      started.current = true;
      activate.mutate(token);
    }
  }, [token, activate]);

  if (!token) {
    return (
      <AuthCard title={t.activate.title}>
        <Alert color="red">{t.activate.missingToken}</Alert>
      </AuthCard>
    );
  }

  const code = activate.error instanceof ApiError ? activate.error.code : undefined;
  return (
    <AuthCard title={t.activate.title}>
      {activate.isPending || activate.isIdle ? (
        <Text role="status">
          <Loader size="xs" mr="xs" />
          {t.activate.working}
        </Text>
      ) : null}
      {code === 'already_active' ? (
        <Stack gap="sm">
          <Alert color="blue">{t.activate.alreadyActive}</Alert>
          <Button component={Link} to="/logowanie">
            {t.nav.login}
          </Button>
        </Stack>
      ) : null}
      {code === 'token_expired' ? (
        <Stack gap="sm">
          <Alert color="orange">{t.activate.expired}</Alert>
          <ResendActivation />
        </Stack>
      ) : null}
      {activate.isError && code !== 'already_active' && code !== 'token_expired' ? (
        <Stack gap="sm">
          <Alert color="red" role="alert">
            {code === 'token_invalid' ? t.activate.invalid : errorMessage(activate.error)}
          </Alert>
          <Anchor component={Link} to="/">
            {t.notFound.back}
          </Anchor>
        </Stack>
      ) : null}
    </AuthCard>
  );
}
